import React, { useState, useEffect, useCallback } from 'react';
import {
  getKiteProfile, getKiteMargins, getKiteHoldings,
  getKitePositions, getKiteOrders, logoutKite,
  getKiteExpiries, startKiteStream, stopKiteStream, getKiteStreamStatus,
} from '../services/apiService';
import '../styles/KiteDashboard.css';

const TABS = ['Analysis', 'Profile', 'Margins', 'Holdings', 'Positions', 'Orders'];

const SYMBOLS = [
  { label: 'NIFTY',       value: 'NIFTY'      },
  { label: 'BANKNIFTY',   value: 'BANKNIFTY'  },
  { label: 'MIDCPNIFTY',  value: 'MIDCPNIFTY' },
  { label: 'FINNIFTY',    value: 'FINNIFTY'   },
  { label: 'NIFTY IT',    value: 'NIFTY IT'   },
  { label: 'SENSEX (BSE)', value: 'SENSEX'    },
  { label: 'BANKEX (BSE)', value: 'BANKEX'    },
];

const fmt = (v, decimals = 2) => {
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  return n.toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const pnlClass = (v) => {
  const n = parseFloat(v);
  if (isNaN(n) || n === 0) return '';
  return n > 0 ? 'kd-positive' : 'kd-negative';
};

const emptyRow = () => ({
  checked: false,
  expiries: [],
  expiry: '',
  expiriesLoading: false,
  expiriesError: null,
  manualExpiry: false,
  manualExpiryDate: '',
});

export default function KiteDashboard({ kiteSession, currentUser, onLogout, onStartAnalysis }) {
  const [tab, setTab] = useState('Analysis');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const token = kiteSession?.accessToken;
  const username = currentUser?.username;

  // Analysis tab state — one row per symbol, all subscribed together on one WS connection
  const [rows, setRows] = useState(() => {
    const r = {};
    SYMBOLS.forEach(s => { r[s.value] = emptyRow(); });
    return r;
  });
  const [liveSubscriptions, setLiveSubscriptions] = useState([]); // [{symbol, expiry}] already streaming
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeMsg, setSubscribeMsg] = useState('');
  const [subscribeResults, setSubscribeResults] = useState(null); // per-symbol results from last subscribe call

  const updateRow = (symbol, patch) =>
    setRows(prev => ({ ...prev, [symbol]: { ...prev[symbol], ...patch } }));

  const load = useCallback(async (t) => {
    if (t === 'Analysis') return;
    if (!token) {
      setError('Kite session token missing — please log in again via Zerodha.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      let result;
      if (t === 'Profile')   result = await getKiteProfile(token, username);
      if (t === 'Margins')   result = await getKiteMargins(token, username);
      if (t === 'Holdings')  result = await getKiteHoldings(token, username);
      if (t === 'Positions') result = await getKitePositions(token, username);
      if (t === 'Orders')    result = await getKiteOrders(token, username);
      setData(prev => ({ ...prev, [t]: result }));
    } catch (e) {
      const status = e.response?.status;
      if (status === 401 || status === 403) {
        setError('Kite session expired (tokens reset at 6 AM IST). Please log in again via Zerodha.');
      } else {
        setError(e.response?.data?.message || e.message || `Failed to load ${t}`);
      }
    } finally {
      setLoading(false);
    }
  }, [token, username]);

  useEffect(() => { load(tab); }, [tab, load]);

  const [statusDenied, setStatusDenied] = useState(false);

  const refreshStreamStatus = useCallback(() => {
    getKiteStreamStatus(username)
      .then(s => setLiveSubscriptions(s.subscriptions || []))
      .catch(e => { if (e.response?.status === 403) setStatusDenied(true); });
  }, [username]);

  // Pick up already-subscribed symbols (e.g. returning from AnalyzerPage) on tab load, then keep
  // polling: after the Zerodha login the backend starts the stream and resolves each index's
  // expiry in a background thread (instrument load takes seconds), so the first call usually
  // lands before it's done; later rollovers / theta switches also change the expiry server-side.
  const streamLive = liveSubscriptions.length > 0;
  useEffect(() => {
    if (tab !== 'Analysis' || statusDenied) return;
    refreshStreamStatus();
    const id = setInterval(() => {
      if (!document.hidden) refreshStreamStatus();
    }, streamLive ? 30000 : 3000);
    return () => clearInterval(id);
  }, [tab, refreshStreamStatus, streamLive, statusDenied]);

  const loadExpiriesFor = useCallback((symbol) => {
    if (!token) return;
    updateRow(symbol, { expiriesLoading: true, expiriesError: null, expiries: [], expiry: '' });
    getKiteExpiries(symbol, token, username)
      .then(list => updateRow(symbol, { expiries: list, expiry: list[0] || '', expiriesLoading: false }))
      .catch(e => {
        const msg = e.response?.data?.error || e.message || 'Failed to load expiries';
        // Auto-fall back to manual entry — expiry lookup fails for symbols the
        // NSE endpoint doesn't carry (e.g. BSE-listed SENSEX/BANKEX).
        updateRow(symbol, { expiriesError: msg, expiriesLoading: false, manualExpiry: true });
      });
  }, [token, username]);

  const toggleSymbol = (symbol, checked) => {
    updateRow(symbol, { checked });
    const row = rows[symbol];
    if (checked && !row.expiries.length && !row.manualExpiry && !row.expiriesLoading) {
      loadExpiriesFor(symbol);
    }
  };

  const selectedEntries = Object.entries(rows)
    .filter(([, r]) => r.checked)
    .map(([symbol, r]) => ({ symbol, expiry: r.manualExpiry ? r.manualExpiryDate : r.expiry }))
    .filter(e => e.expiry);

  const handleSubscribe = async () => {
    if (!selectedEntries.length) {
      setSubscribeMsg('Select at least one symbol with a valid expiry.');
      return;
    }
    setSubscribing(true);
    setSubscribeMsg('');
    setSubscribeResults(null);
    try {
      const res = await startKiteStream(selectedEntries, token, username);
      setSubscribeResults(res.results || null);
      setSubscribeMsg(`Subscribed to ${res.subscribed} instruments across ${res.symbols} symbol(s).`);
      refreshStreamStatus();
    } catch (e) {
      setSubscribeMsg(e.response?.data?.error || e.message || 'Failed to subscribe');
    } finally {
      setSubscribing(false);
    }
  };

  const handleOpenSymbol = (sub) => {
    onStartAnalysis?.({
      symbol: sub.symbol,
      expiryDate: sub.expiry,
      strikePrice: 0,
      refreshInterval: 5000,
      support: 0,
      resistance: 0,
      kiteMode: true,
      autoMode: true,
    });
  };

  const handleStopAll = async () => {
    try { await stopKiteStream(username); } catch (_) {}
    setLiveSubscriptions([]);
    setSubscribeMsg('');
    setSubscribeResults(null);
  };

  const handleLogout = async () => {
    try { await logoutKite(token, username); } catch (_) {}
    onLogout();
  };

  return (
    <div className="kd-root">
      <header className="kd-header">
        <div className="kd-header-left">
          <span className="kd-zerodha-badge">Z</span>
          <div>
            <div className="kd-user-name">{kiteSession?.userName || kiteSession?.userId}</div>
            <div className="kd-user-sub">{kiteSession?.email} · {kiteSession?.broker}</div>
          </div>
        </div>
        <div className="kd-header-right">
          <div className="kd-exchanges" title="Exchanges enabled on your Kite account (informational)">
            {(kiteSession?.exchanges || []).map(e => (
              <span key={e} className="kd-exchange-tag">{e}</span>
            ))}
          </div>
          <button className="kd-logout-btn" onClick={handleLogout}>Logout</button>
        </div>
      </header>

      <nav className="kd-tabs">
        {TABS.map(t => (
          <button
            key={t}
            className={`kd-tab${tab === t ? ' kd-tab-active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
        <button className="kd-tab kd-tab-refresh" onClick={() => (tab === 'Analysis' ? refreshStreamStatus() : load(tab))}>↻ Refresh</button>
      </nav>

      <main className="kd-body">
        {tab === 'Analysis' && (
          <div className="kd-analysis-setup">
            <h3 className="kd-analysis-title">Option Chain Analysis</h3>
            <p className="kd-analysis-sub">
              Check the indices you want to track and set an expiry for each — all are
              subscribed together on a single Kite WebSocket connection (Kite allows up
              to 3000 instruments per connection, plenty for every index at once). Once
              subscribed, open any of them below; switching symbols never restarts the stream.
            </p>

            {liveSubscriptions.length > 0 && (
              <div className="kd-live-sessions">
                <div className="kd-live-sessions-title">Live on the shared connection</div>
                {/* SELLER rows are extra chains streamed for the range-day seller — shown on
                    their index's tracked row, not opened as a dashboard of their own. */}
                {liveSubscriptions.filter(sub => sub.role !== 'SELLER').map(sub => (
                  <div key={`${sub.symbol}|${sub.expiry}`} className="kd-live-session-row">
                    <span className="kd-live-dot" />
                    <span className="kd-live-symbol">{sub.symbol}</span>
                    <span className="kd-live-expiry" title="Tracked expiry — analysis and option buying">{sub.expiry}</span>
                    {sub.sellerExpiry && (
                      <span className="kd-live-expiry" title="Range-day option seller's chain">
                        🧊 seller: {sub.sellerExpiry}
                      </span>
                    )}
                    <button className="kd-open-btn" onClick={() => handleOpenSymbol(sub)}>
                      Open →
                    </button>
                  </div>
                ))}
                <button className="kd-stop-btn kd-stop-all-btn" onClick={handleStopAll}>
                  ⏹ Stop All Streams
                </button>
              </div>
            )}

            <div className="kd-analysis-form">
              <div className="kd-symbol-checklist">
                {SYMBOLS.map(s => {
                  const row = rows[s.value];
                  return (
                    <div key={s.value} className="kd-symbol-row">
                      <label className="kd-symbol-checkbox">
                        <input
                          type="checkbox"
                          checked={row.checked}
                          onChange={e => toggleSymbol(s.value, e.target.checked)}
                          disabled={subscribing}
                        />
                        {s.label}
                      </label>

                      {row.checked && (
                        <div className="kd-symbol-expiry">
                          {row.manualExpiry
                            ? <input
                                type="date"
                                className="kd-select"
                                value={row.manualExpiryDate}
                                onChange={e => updateRow(s.value, { manualExpiryDate: e.target.value })}
                                disabled={subscribing}
                              />
                            : row.expiriesLoading
                              ? <span className="kd-loading-small">Loading expiries…</span>
                              : row.expiriesError
                                ? <div className="kd-expiry-error">
                                    <span>{row.expiriesError}</span>
                                    <button className="kd-retry-btn" onClick={() => loadExpiriesFor(s.value)}>Retry</button>
                                  </div>
                                : <select
                                    value={row.expiry}
                                    onChange={e => updateRow(s.value, { expiry: e.target.value })}
                                    className="kd-select"
                                    disabled={!row.expiries.length || subscribing}
                                  >
                                    {row.expiries.length === 0
                                      ? <option value="">No expiries — click Retry</option>
                                      : row.expiries.map(ex => <option key={ex} value={ex}>{ex}</option>)
                                    }
                                  </select>
                          }
                          <label className="kd-manual-expiry-toggle">
                            <input
                              type="checkbox"
                              checked={row.manualExpiry}
                              onChange={e => updateRow(s.value, { manualExpiry: e.target.checked })}
                              disabled={subscribing}
                            />
                            Enter manually
                          </label>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {subscribeMsg && (
                <div className={`kd-stream-msg ${subscribeResults?.some(r => r.status === 'failed') ? 'kd-stream-error' : 'kd-stream-ok'}`}>
                  {subscribeMsg}
                  {subscribeResults?.filter(r => r.status === 'failed').map(r => (
                    <div key={r.symbol} className="kd-stream-msg-detail">{r.symbol}: {r.error}</div>
                  ))}
                </div>
              )}

              <div className="kd-analysis-actions">
                <button
                  className="kd-start-btn"
                  onClick={handleSubscribe}
                  disabled={!selectedEntries.length || subscribing}
                >
                  {subscribing ? 'Subscribing…' : `▶ Subscribe Selected (${selectedEntries.length})`}
                </button>
              </div>

              <div className="kd-analysis-note">
                <strong>Data sources:</strong> LTP · OI · Volume streamed live via Kite WebSocket.
                Implied Volatility fetched from NSE every 60 s per symbol — GEX and Greeks become
                active after the first refresh cycle for that symbol.
              </div>
            </div>
          </div>
        )}

        {tab !== 'Analysis' && loading && <div className="kd-loading">Loading {tab}…</div>}
        {tab !== 'Analysis' && error   && <div className="kd-error">{error}</div>}
        {tab !== 'Analysis' && !loading && !error && data[tab] && (
          <>
            {tab === 'Profile'   && <ProfileView   d={data[tab]} />}
            {tab === 'Margins'   && <MarginsView   d={data[tab]} />}
            {tab === 'Holdings'  && <HoldingsView  d={data[tab]} />}
            {tab === 'Positions' && <PositionsView d={data[tab]} />}
            {tab === 'Orders'    && <OrdersView    d={data[tab]} />}
          </>
        )}
      </main>
    </div>
  );
}

function ProfileView({ d }) {
  const rows = [
    ['User ID', d.user_id], ['Name', d.user_name], ['Email', d.email],
    ['Type', d.user_type], ['Broker', d.broker], ['Login time', d.login_time],
    ['Products', (d.products || []).join(', ')],
    ['Order types', (d.order_types || []).join(', ')],
  ];
  return (
    <div className="kd-profile">
      {rows.map(([k, v]) => (
        <div key={k} className="kd-profile-row">
          <span className="kd-profile-key">{k}</span>
          <span className="kd-profile-val">{v || '—'}</span>
        </div>
      ))}
    </div>
  );
}

function MarginsView({ d }) {
  const segments = d && typeof d === 'object' ? Object.entries(d) : [];
  return (
    <div className="kd-margins">
      {segments.map(([seg, m]) => (
        <div key={seg} className="kd-margin-card">
          <div className="kd-margin-seg">{seg.toUpperCase()}</div>
          <div className="kd-margin-grid">
            <MRow label="Available Cash" value={fmt(m?.available?.cash)} />
            <MRow label="Available Collateral" value={fmt(m?.available?.collateral)} />
            <MRow label="Net" value={fmt(m?.net)} />
            <MRow label="Used Debits" value={fmt(m?.utilised?.debits)} />
            <MRow label="Used Span" value={fmt(m?.utilised?.span)} />
            <MRow label="Used Exposure" value={fmt(m?.utilised?.exposure)} />
          </div>
        </div>
      ))}
    </div>
  );
}
function MRow({ label, value }) {
  return (
    <div className="kd-mrow">
      <span className="kd-mrow-label">{label}</span>
      <span className="kd-mrow-value">₹{value}</span>
    </div>
  );
}

function HoldingsView({ d }) {
  const rows = Array.isArray(d) ? d : [];
  if (!rows.length) return <div className="kd-empty">No holdings found.</div>;
  const totalPnl = rows.reduce((s, r) => s + (parseFloat(r.pnl) || 0), 0);
  return (
    <div className="kd-table-wrap">
      <div className={`kd-pnl-banner ${pnlClass(totalPnl)}`}>
        Total P&amp;L: ₹{fmt(totalPnl)}
      </div>
      <table className="kd-table">
        <thead>
          <tr>
            <th>Symbol</th><th>Qty</th><th>Avg Price</th>
            <th>LTP</th><th>P&amp;L</th><th>Day Chg%</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="kd-symbol">{r.tradingsymbol}</td>
              <td>{r.quantity}</td>
              <td>₹{fmt(r.average_price)}</td>
              <td>₹{fmt(r.last_price)}</td>
              <td className={pnlClass(r.pnl)}>₹{fmt(r.pnl)}</td>
              <td className={pnlClass(r.day_change_percentage)}>{fmt(r.day_change_percentage)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PositionsView({ d }) {
  const net = Array.isArray(d?.net) ? d.net : [];
  const day = Array.isArray(d?.day) ? d.day : [];
  const rows = net.length ? net : day;
  if (!rows.length) return <div className="kd-empty">No open positions.</div>;
  const totalPnl = rows.reduce((s, r) => s + (parseFloat(r.pnl) || 0), 0);
  return (
    <div className="kd-table-wrap">
      <div className={`kd-pnl-banner ${pnlClass(totalPnl)}`}>
        Net P&amp;L: ₹{fmt(totalPnl)}
      </div>
      <table className="kd-table">
        <thead>
          <tr>
            <th>Symbol</th><th>Product</th><th>Qty</th>
            <th>Avg</th><th>LTP</th><th>P&amp;L</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="kd-symbol">{r.tradingsymbol}</td>
              <td>{r.product}</td>
              <td className={r.quantity < 0 ? 'kd-negative' : ''}>{r.quantity}</td>
              <td>₹{fmt(r.average_price)}</td>
              <td>₹{fmt(r.last_price)}</td>
              <td className={pnlClass(r.pnl)}>₹{fmt(r.pnl)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OrdersView({ d }) {
  const rows = Array.isArray(d) ? d : [];
  if (!rows.length) return <div className="kd-empty">No orders today.</div>;
  const statusColor = (s) => {
    if (s === 'COMPLETE') return 'kd-positive';
    if (s === 'REJECTED' || s === 'CANCELLED') return 'kd-negative';
    return 'kd-neutral';
  };
  return (
    <div className="kd-table-wrap">
      <table className="kd-table">
        <thead>
          <tr>
            <th>Time</th><th>Symbol</th><th>Type</th>
            <th>Side</th><th>Qty</th><th>Price</th><th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="kd-time">{(r.order_timestamp || '').slice(11, 19)}</td>
              <td className="kd-symbol">{r.tradingsymbol}</td>
              <td>{r.order_type}</td>
              <td className={r.transaction_type === 'BUY' ? 'kd-positive' : 'kd-negative'}>
                {r.transaction_type}
              </td>
              <td>{r.quantity}</td>
              <td>₹{fmt(r.price || r.average_price)}</td>
              <td className={statusColor(r.status)}>{r.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
